import { Gift, Pencil, Trash2 } from 'lucide-react';
import type { Product } from '@/shared/types/product';
import { allowsRedemption, productText } from '../model/catalog-scope';
import { Badge } from '@/shared/ui/badge';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/shared/ui/dropdown-menu';
import { ListCardActions } from '@/shared/components/list-card-actions';
import { SelectableCard } from '@/shared/components/selectable-card';

const REDEMPTION_LABELS: Record<string, string> = {
  product: 'Produto',
  discount: 'Desconto',
  courtesy: 'Cortesia',
  birthday: 'Aniversário',
};

interface ProductCardProps {
  product: Product;
  selected: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onRemove: () => void;
}

export function ProductCard({
  product,
  selected,
  onToggle,
  onEdit,
  onRemove,
}: ProductCardProps) {
  const typeLabel = REDEMPTION_LABELS[product.redemptionType ?? 'product'] ?? 'Produto';
  const price = product.price != null && Number(product.price) > 0 ? String(product.price) : null;
  const name = productText(product.name) || 'Sem nome';
  const isRedemption = allowsRedemption(product);

  return (
    <SelectableCard
      selected={selected}
      onToggle={onToggle}
      enter={false}
      ariaLabel={`Selecionar produto ${name}`}
      onCardClick={onEdit}
      actions={
        <ListCardActions
          menuChildren={
            <>
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil />
                Editar
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onRemove} destructive>
                <Trash2 />
                Remover
              </DropdownMenuItem>
            </>
          }
        />
      }
    >
      <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold sm:text-base">{name}</p>
          <p className="mt-0.5 truncate text-xs text-muted-foreground">
            SKU <span className="tabular-nums">{productText(product.uniqueId) || '—'}</span>
            {price ? <> · R$ {price}</> : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 sm:justify-end sm:gap-2">
          <Badge variant="default" className="h-7 gap-1 px-2.5 font-semibold tabular-nums">
            {productText(product.value) || '0'}
            <span className="text-[10px] font-medium uppercase tracking-wide opacity-80">pts</span>
          </Badge>
          <Badge variant={isRedemption ? 'success' : 'secondary'} className="h-7 gap-1 font-normal">
            {isRedemption ? <Gift className="size-3" /> : null}
            {isRedemption ? 'Resgate' : 'Sem resgate'}
          </Badge>
          <Badge variant="outline" className="h-7 font-normal">
            {typeLabel}
          </Badge>
        </div>
      </div>
    </SelectableCard>
  );
}
